import { ElectronService } from '@app/electron/electron.service';
import { AuthTokenAdapter } from '@shared/adapters/auth-token-adapter';
import { createReconnectingSocket, SocketAdapter } from '@shared/adapters/socket-adapter';
import { environment } from '@shared/environments/environment';
import { ElectronChannel, ElectronInvocationChannel } from '@shared/types';
import { ReconnectingWebSocket } from '@shared/utils/reconnecting-websocket';
import { Subject } from 'rxjs';

import { RendererAppLifecycle } from './renderer-app-lifecycle';

class RendererSocket implements SocketAdapter {
  private readonly eventsSubject = new Subject<{ event: string; payload?: unknown }>();
  public events$ = this.eventsSubject.asObservable();

  // Browser-mode socket. In Electron the real socket lives in the main process
  // and this adapter is just an IPC proxy.
  private ws?: ReconnectingWebSocket;

  // In the browser this adapter owns the real socket; in Electron the socket lives
  // in the main process and this is only an IPC proxy.
  public readonly ownsConnection: boolean;

  constructor(
    private electronService: ElectronService,
    private authTokenAdapter: AuthTokenAdapter
  ) {
    const isElectron = this.electronService.isRunningInElectron;
    this.ownsConnection = !isElectron;
    console.log('[RENDERER-SOCKET] Initializing RendererSocketAdapter', { isElectron });

    if (isElectron) {
      console.log('[RENDERER-SOCKET] Running in Electron - setting up IPC listeners');
      // Listen for events from main process via IPC
      this.electronService.on(ElectronChannel.SocketEvent, (data: { event: string; payload?: unknown }) => {
        console.log('[RENDERER-SOCKET] Received event from main process:', data.event);
        this.eventsSubject.next(data);
      });
    } else {
      console.log('[RENDERER-SOCKET] Running in browser - will use direct WebSocket');
    }
  }

  public async connect(url: string, resetCounter?: boolean): Promise<void> {
    const maskedUrl = url.replace(/token=[^&]+/, 'token=***');

    if (this.electronService.isRunningInElectron) {
      console.log('[RENDERER-SOCKET] Connecting via IPC to main process:', maskedUrl, { resetCounter });
      try {
        await this.electronService.invoke(ElectronInvocationChannel.WebSocketConnect, { url, resetCounter });
        console.log('[RENDERER-SOCKET] IPC connect() completed');
      } catch (error) {
        console.error('[RENDERER-SOCKET] Failed to connect via IPC:', error);
        throw error;
      }
    } else {
      console.log('[RENDERER-SOCKET] Connecting directly in browser:', maskedUrl, { resetCounter });
      // Direct WebSocket in browser
      await this.connectDirectly();
    }
  }

  public async send(event: string, payload: unknown): Promise<void> {
    if (this.electronService.isRunningInElectron) {
      console.log('[RENDERER-SOCKET] Sending via IPC to main process:', event);
      try {
        await this.electronService.invoke(ElectronInvocationChannel.WebSocketSend, { event, payload });
      } catch (error) {
        console.error('[RENDERER-SOCKET] Failed to send via IPC:', error);
        throw error;
      }
    } else {
      // Direct send in browser. Sends are logged once, by SharedSocketService.
      const message = JSON.stringify({ event, payload });
      // ReconnectingWebSocket.send() handles the connectivity check
      this.ws?.send(message);
    }
  }

  public async disconnect(): Promise<void> {
    if (this.electronService.isRunningInElectron) {
      console.log('[RENDERER-SOCKET] Disconnecting via IPC to main process');
      try {
        await this.electronService.invoke(ElectronInvocationChannel.WebSocketDisconnect, undefined);
        console.log('[RENDERER-SOCKET] IPC disconnect() completed');
      } catch (error) {
        console.error('[RENDERER-SOCKET] Failed to disconnect via IPC:', error);
        throw error;
      }
    } else {
      console.log('[RENDERER-SOCKET] Disconnecting WebSocket directly');
      if (this.ws) {
        console.log('[RENDERER-SOCKET] Closing WebSocket, current state:', this.ws.readyState);
        this.ws.close();
        // Null out so the next connect() creates a fresh loop instead of no-op'ing.
        this.ws = undefined;
      } else {
        console.log('[RENDERER-SOCKET] No WebSocket to close');
      }
    }
  }

  public async readyState(): Promise<WebSocket['readyState']> {
    if (this.electronService.isRunningInElectron) {
      try {
        const readyState = await this.electronService.invoke(ElectronInvocationChannel.WebSocketReadyState, undefined);
        console.log('[RENDERER-SOCKET] WebSocket ready state:', readyState);
        return readyState;
      } catch (error) {
        console.error('[RENDERER-SOCKET] Failed to check if WebSocket is connected:', error);
        return WebSocket.CLOSED;
      }
    } else {
      return this.ws?.readyState ?? WebSocket.CLOSED;
    }
  }

  /**
   * Browser-mode connect. The first call creates the ReconnectingWebSocket and
   * starts it; subsequent calls are a no-op because the loop owns its own
   * reconnects and pulls a fresh token before each attempt (so a token refresh
   * does not need to push a new URL here).
   */
  private async connectDirectly(): Promise<void> {
    if (this.ws) {
      console.log('[RENDERER-SOCKET] Socket already started - ignoring connect (loop self-manages reconnects)');
      return;
    }

    console.log('[RENDERER-SOCKET] Creating ReconnectingWebSocket');
    this.ws = createReconnectingSocket(
      () => this.buildUrl(),
      new RendererAppLifecycle(),
      (event, payload) => {
        // Arrival is logged once, by SharedSocketService.
        this.eventsSubject.next({ event, payload });
      }
    );
  }

  /** Resolves a fresh, token-bearing event-websocket URL for each connect attempt. */
  private async buildUrl(): Promise<string> {
    const { accessToken } = await this.authTokenAdapter.ensureValidAccessToken();
    return `${environment.websocketUrlWeb}?token=${accessToken}&appType=web`;
  }
}

export default RendererSocket;
